import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EmailIcon from '@mui/icons-material/Email';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PinIcon from '@mui/icons-material/Pin';
import SendIcon from '@mui/icons-material/Send';
import SmsIcon from '@mui/icons-material/Sms';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VerifiedIcon from '@mui/icons-material/Verified';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import React from 'react';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { QrCode } from '../../components/alliance/qr-code';
import {
  getUserAttribute,
  isMfaMethod,
  MFA_METHODS,
  MFA_STATUS_KEY,
  MfaMethod,
  useMfaStatus,
} from '../../hooks/use-mfa-status';
import { captureError } from '../../utils/capture-error';
import copy from '../../utils/copy-to-clipboard';

/** Short label for a method (the drawer's toggle and status chips) */
export const getOptionName = (option: string) => {
  switch (option) {
    case 'SMS_MFA':
      return 'SMS';
    case 'SOFTWARE_TOKEN_MFA':
      return 'TOTP';
    case 'EMAIL_OTP':
      return 'EMAIL';
    default:
      return option;
  }
};

/**
 * Pulls the message off a rejected Cognito/Amplify error. Those reject with
 * either an Error or a plain object carrying code/name/message, and the message
 * is what distinguishes a wrong code from an expired session or an attempt
 * limit — so show it rather than a generic failure.
 */
const getErrorMessage = (params: { error: unknown }): string => {
  // Params
  const { error } = params;

  const message = (error as { message?: unknown } | null)?.message;

  return typeof message === 'string' && message
    ? message
    : 'An error has occurred!';
};

const createOtpAuthUrl = (params: {
  issuer: string;
  account: string;
  secret: string;
}): string => {
  // Params
  const { issuer, account, secret } = params;

  const encodedIssuer = encodeURIComponent(issuer);
  const encodedAccount = encodeURIComponent(account);
  const encodedSecret = encodeURIComponent(secret);

  return `otpauth://totp/${encodedIssuer}:${encodedAccount}?secret=${encodedSecret}&issuer=${encodedIssuer}`;
};

/**
 * The "Update MFA" drawer: pick Email, SMS or an authenticator app and set
 * it up. Opened from the Agent Profile control (MultiFactorInfo) and from
 * the MFA System Screen's methods card.
 *
 * Saving a method also refreshes the sign-in gate's MFA check, so on the MFA
 * System Screen the app continues by itself.
 *
 * @param user - The signed-in Cognito user
 * @param open - Show the drawer
 * @param onClose - Called when the drawer closes (never while saving)
 * @param initialMethod - Method to show on open (default: the preferred
 *   method, else SMS)
 */
export function MfaSetupDrawer(props: {
  user: CognitoUser;
  open: boolean;
  onClose: () => void;
  initialMethod?: MfaMethod;
}) {
  // Props
  const { user, open, initialMethod } = props;
  // State
  const [selected, setSelected] = React.useState<MfaMethod>('SMS_MFA');
  const [phone, setPhone] = React.useState('');
  const [verifySMSCode, setVerifySMSCode] = React.useState('');
  const [verifyTOTPCode, setVerifyTOTPCode] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  // Hooks
  const queryClient = useQueryClient();
  const query = useMfaStatus();

  const preferred = query.data?.preferred;
  const hasPreferred = isMfaMethod(preferred);

  React.useEffect(() => {
    if (open) {
      // Start on the method that was asked for, else the current one
      setSelected(
        initialMethod || (isMfaMethod(preferred) ? preferred : 'SMS_MFA'),
      );
    }
    // Only when the drawer opens: a save mustn't switch the tab under them
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    if (open) {
      // Reset State when the drawer is opened or they click on a different MFA type
      setPhone('');
      setVerifySMSCode('');
      setVerifyTOTPCode('');
      setCopied(false);
    }
  }, [open, selected]);

  /**
   * Refetch the user's methods and the sign-in gate's check, which lets the
   * app continue from the MFA System Screen
   */
  const refreshStatus = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: MFA_STATUS_KEY }),
      queryClient.invalidateQueries({
        queryKey: ['check_Auth.getPreferredMFA'],
      }),
    ]);
  };

  // Query - Get code for Authenticator App
  // Key on the username, never the CognitoUser object — it serializes its
  // localStorage reference, so any unrelated write changes the key and
  // regenerates the secret out from under the user mid-setup.
  //
  // exhaustive-deps wants `user` in the key because the queryFn closes over it,
  // which is what put the object there originally and caused that bug. The
  // username is `user`'s stable identity for this query, and it is already in
  // the key, so the rule is satisfied in substance and suppressed in form.
  const queryTOTPCode = useQuery({
    enabled: open && selected === 'SOFTWARE_TOKEN_MFA',
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['Auth.setupTOTP', { username: user.getUsername() }],
    queryFn: async () => {
      const secretCode = await Auth.setupTOTP(user);
      return secretCode;
    },
    // The secret stays valid until verified, so don't refetch it. Retrying
    // would mint a second secret and invalidate the QR code already on screen
    staleTime: Infinity,
    retry: false,
    onError: (error) => captureError({ data: { error } }),
  });

  // Mutation - Verify and Set MFA for Authenticator App
  const mutationTOTPVerify = useMutation({
    mutationFn: async () => {
      // Verify code generated from users Authenticator App
      await Auth.verifyTotpToken(user, verifyTOTPCode);
      // Set Authenticator App as their preferred MFA
      await Auth.setPreferredMFA(user, 'SOFTWARE_TOKEN_MFA');
    },
    onSuccess: async () => {
      // Refetch preferred MFA (and let the gate continue)
      await refreshStatus();
      // Reset code
      setVerifyTOTPCode('');
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Mutation - Add and Send code for SMS
  const mutationSMSAdd = useMutation({
    mutationFn: async () => {
      // Add the users phone number to their Cognito account
      await Auth.updateUserAttributes(user, { phone_number: `+1${phone}` });
      // Send code to the phone number they added
      await Auth.verifyUserAttribute(user, 'phone_number');
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Mutation - Verify and Set MFA for SMS
  const mutationSMSVerify = useMutation({
    mutationFn: async () => {
      // Verify code sent to the user's phone
      await Auth.verifyCurrentUserAttributeSubmit(
        'phone_number',
        verifySMSCode,
      );
      // Set SMS as their preferred MFA method
      await Auth.setPreferredMFA(user, 'SMS_MFA');
    },
    onSuccess: async () => {
      // Refetch preferred MFA (and let the gate continue)
      await refreshStatus();
      // Reset phone
      setPhone('');
      setVerifySMSCode('');
      // Reset add mutation
      mutationSMSAdd.reset();
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Mutation - Set Preferred MFA
  const mutationEmail = useMutation({
    mutationFn: async () => {
      // Get Token for Request
      const AccessToken = (await Auth.currentSession())
        .getAccessToken()
        .getJwtToken();

      // Direct call to Cognito API for setting MFA as Email
      const response = await fetch(
        'https://cognito-idp.us-east-1.amazonaws.com/',
        {
          body: JSON.stringify({
            AccessToken,
            EmailMfaSettings: { Enabled: true, PreferredMfa: true },
          }),
          headers: {
            // Important: These extra headers must be set for AWS to accept the call
            'x-amz-target':
              'AWSCognitoIdentityProviderService.SetUserMFAPreference',
            'x-amz-user-agent': 'aws-amplify/5.0.4 js',
            'Content-Type': 'application/x-amz-json-1.1',
          },
          method: 'POST',
        },
      );

      // fetch only rejects on a network failure, so without this a rejected
      // request reports success and leaves the user with no MFA set
      if (!response.ok) {
        const data = await response.json().catch(() => null);

        throw new Error(
          data?.message || `Cognito responded with ${response.status}`,
        );
      }
    },
    onSuccess: async () => {
      // Refetch preferred MFA (and let the gate continue)
      await refreshStatus();
    },
    onError: (error) => captureError({ data: { error } }),
  });

  const isPending =
    mutationEmail.isLoading ||
    mutationSMSAdd.isLoading ||
    mutationSMSVerify.isLoading ||
    mutationTOTPVerify.isLoading;

  const handleClose = () => {
    if (!isPending) {
      props.onClose();

      // Reset some state when closing the drawer
      setPhone('');
      setVerifySMSCode('');
      setVerifyTOTPCode('');
      setCopied(false);

      // Reset mutation functions
      mutationEmail.reset();
      mutationSMSAdd.reset();
      mutationSMSVerify.reset();
      mutationTOTPVerify.reset();
    }
  };

  return (
    <Drawer
      container={window.document.body}
      variant="temporary"
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: { xs: '100vw', sm: '80vw', md: '60vw', lg: '40vw' },
          height: '100dvh',
        },
      }}
    >
      <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
        <Toolbar>
          <IconButton aria-label="close" sx={{ mr: 2 }} onClick={handleClose}>
            {isPending ? <CircularProgress size={24} /> : <CloseIcon />}
          </IconButton>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ fontSize: 22, fontWeight: 'normal' }}
            >
              Update MFA
            </Typography>
          </Box>

          {hasPreferred ? (
            <Chip
              avatar={<TaskAltIcon fontSize="small" color="inherit" />}
              label={getOptionName(preferred || '')}
              size="small"
              color="success"
              variant="outlined"
            />
          ) : null}
        </Toolbar>

        <Divider />

        <Box sx={{ flex: 1, overflow: 'auto', padding: 2 }}>
          <Stack spacing={1}>
            {query.data && !hasPreferred ? (
              <Box
                sx={{
                  fontWeight: 'bold',
                  fontSize: 12,
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  color: '#4a4a4a',
                }}
              >
                Select from the following options
              </Box>
            ) : null}

            <ToggleButtonGroup
              fullWidth
              exclusive
              color="info"
              value={selected}
              onChange={(event, newValue) => {
                if (newValue) {
                  setSelected(newValue);

                  // Reset mutation functions
                  mutationEmail.reset();
                  mutationSMSAdd.reset();
                  mutationSMSVerify.reset();
                  mutationTOTPVerify.reset();
                }
              }}
            >
              {MFA_METHODS.map((mfaOption) => {
                return (
                  <ToggleButton key={mfaOption} value={mfaOption}>
                    {getOptionName(mfaOption)}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>

            {hasPreferred && preferred === selected ? (
              <Alert severity="success">
                This option is set as your preferred MFA method
              </Alert>
            ) : null}

            {selected === 'EMAIL_OTP' ? (
              <Stack spacing={1}>
                <Paper
                  elevation={0}
                  sx={{ p: 1, border: '1px solid #00000022' }}
                >
                  <Stack spacing={1} direction="row">
                    <HelpOutlineIcon fontSize="small" color="info" />

                    <Box sx={{ fontSize: 14, color: '#212121' }}>
                      Email-based multi-factor authentication involves sending a
                      one-time verification code to the user's registered email
                      address. The user must retrieve the code to confirm their
                      identity during login.
                    </Box>
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={{ border: '1px solid #00000022' }}>
                  <Box sx={{ p: 1 }}>
                    <Stack spacing={1} direction="row">
                      <Box>
                        <Box
                          sx={{
                            fontWeight: 'bold',
                            fontFamily: 'Roboto Mono',
                            border: '1px solid #000',
                            borderRadius: 50,
                            height: 22,
                            width: 22,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          1
                        </Box>
                      </Box>

                      <Box sx={{ pb: 1 }}>
                        If your email address needs to be updated please contact
                        Help desk for assistance. Otherwise click the button
                        below to set Email as your preferred MFA option.
                      </Box>
                    </Stack>

                    <Box
                      sx={{
                        p: 1,
                        backgroundColor: '#f1f1f1',
                        borderRadius: 2,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-evenly"
                      >
                        <AlternateEmailIcon color="info" />

                        <Box sx={{ fontFamily: 'Roboto Mono' }}>
                          {getUserAttribute({
                            status: query.data,
                            name: 'email',
                          })}
                        </Box>

                        {getUserAttribute({
                          status: query.data,
                          name: 'email_verified',
                        }) === 'true' ? (
                          <Tooltip title="Verified" placement="left" arrow>
                            <VerifiedIcon color="success" />
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </Box>
                  </Box>

                  <Divider />

                  <Box sx={{ p: 1 }}>
                    <Stack spacing={1}>
                      {mutationEmail.isError ? (
                        <Alert
                          severity="error"
                          onClose={() => mutationEmail.reset()}
                        >
                          {getErrorMessage({ error: mutationEmail.error })}
                        </Alert>
                      ) : null}

                      <Button
                        disabled={
                          mutationEmail.isLoading || preferred === 'EMAIL_OTP'
                        }
                        fullWidth
                        variant="contained"
                        disableElevation
                        size="large"
                        color="info"
                        startIcon={<EmailIcon />}
                        onClick={() => mutationEmail.mutate()}
                      >
                        {mutationEmail.isLoading
                          ? 'Updating MFA...'
                          : 'Set email as MFA'}
                      </Button>

                      {mutationEmail.isSuccess ? (
                        <Alert severity="success">
                          Preference has been set!
                        </Alert>
                      ) : null}
                    </Stack>
                  </Box>
                </Paper>
              </Stack>
            ) : null}

            {selected === 'SMS_MFA' ? (
              <Stack spacing={1}>
                {preferred === 'SMS_MFA' &&
                getUserAttribute({
                  status: query.data,
                  name: 'phone_number_verified',
                }) ? (
                  <Alert severity="info">
                    You will receive messages at{' '}
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      {getUserAttribute({
                        status: query.data,
                        name: 'phone_number',
                      })}
                    </Box>
                  </Alert>
                ) : null}

                <Paper
                  elevation={0}
                  sx={{ p: 1, border: '1px solid #00000022' }}
                >
                  <Stack spacing={1} direction="row">
                    <HelpOutlineIcon fontSize="small" color="info" />

                    <Box sx={{ fontSize: 14, color: '#212121' }}>
                      SMS-based multi-factor authentication sends a one-time
                      verification code via text message to the user's
                      registered mobile phone. The user inputs this code to
                      verify their identity.
                    </Box>
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={{ border: '1px solid #00000022' }}>
                  <Box sx={{ p: 1 }}>
                    <Stack spacing={1}>
                      <Stack spacing={1} direction="row">
                        <Box>
                          <Box
                            sx={{
                              fontWeight: 'bold',
                              fontFamily: 'Roboto Mono',
                              border: '1px solid #000',
                              borderRadius: 50,
                              height: 22,
                              width: 22,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            1
                          </Box>
                        </Box>

                        <Box sx={{ pb: 1 }}>
                          Add the phone number where you would like to receive
                          messages while signing in. A code will be sent to your
                          phone to verify that the number is correct.
                        </Box>
                      </Stack>

                      <TextField
                        id="multi-factor-info-phone"
                        fullWidth
                        type="tel"
                        autoComplete="off"
                        placeholder="0000000000"
                        label="Phone Number (+15551234567)"
                        value={phone}
                        onChange={(event) => {
                          setPhone(event.target.value.replace(/\D/g, ''));
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          style: { fontFamily: 'Roboto Mono', fontSize: 18 },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box
                                sx={{
                                  fontFamily: 'Roboto Mono',
                                  fontWeight: 'bold',
                                  fontSize: 20,
                                }}
                              >
                                +1
                              </Box>
                            </InputAdornment>
                          ),
                        }}
                      />

                      {mutationSMSAdd.isError ? (
                        <Alert
                          severity="error"
                          onClose={() => mutationSMSAdd.reset()}
                        >
                          {getErrorMessage({ error: mutationSMSAdd.error })}
                        </Alert>
                      ) : null}

                      <Button
                        disabled={
                          mutationSMSAdd.isSuccess ||
                          mutationSMSAdd.isLoading ||
                          mutationSMSAdd.isError ||
                          !phone.trim() ||
                          !isMobilePhone(`+1${phone}`, 'en-US')
                        }
                        fullWidth
                        disableElevation
                        variant="contained"
                        color="info"
                        startIcon={<SendIcon />}
                        onClick={() => mutationSMSAdd.mutate()}
                      >
                        {mutationSMSAdd.isLoading
                          ? 'Sending code...'
                          : 'Send code to your phone'}
                      </Button>

                      {mutationSMSAdd.isSuccess ? (
                        <Alert severity="success">
                          Please wait for a code to be sent to your phone
                        </Alert>
                      ) : null}
                    </Stack>
                  </Box>

                  <Divider />

                  <Box sx={{ p: 1 }}>
                    <Stack spacing={1}>
                      <Stack spacing={1} direction="row">
                        <Box>
                          <Box
                            sx={{
                              fontWeight: 'bold',
                              fontFamily: 'Roboto Mono',
                              border: '1px solid #000',
                              borderRadius: 50,
                              height: 22,
                              width: 22,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            2
                          </Box>
                        </Box>

                        <Box sx={{ pb: 1 }}>
                          Once you have received a text message with your code
                          add it here to verify that everything is setup
                          correctly.
                        </Box>
                      </Stack>

                      <TextField
                        disabled={!mutationSMSAdd.isSuccess}
                        id="multi-factor-info-phone-verify"
                        fullWidth
                        placeholder="000000"
                        autoComplete="one-time-code"
                        label="Code from SMS message"
                        value={verifySMSCode}
                        onChange={(event) => {
                          setVerifySMSCode(
                            event.target.value.replace(/\D/g, ''),
                          );
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { fontFamily: 'Roboto Mono' } }}
                      />

                      {mutationSMSVerify.isError ? (
                        <Alert
                          severity="error"
                          onClose={() => mutationSMSVerify.reset()}
                        >
                          {getErrorMessage({
                            error: mutationSMSVerify.error,
                          })}
                        </Alert>
                      ) : null}

                      <Button
                        disabled={
                          !mutationSMSAdd.isSuccess ||
                          mutationSMSVerify.isLoading ||
                          mutationSMSVerify.isError ||
                          !verifySMSCode.trim()
                        }
                        fullWidth
                        variant="contained"
                        disableElevation
                        size="large"
                        color="info"
                        startIcon={<SmsIcon />}
                        onClick={() => mutationSMSVerify.mutate()}
                      >
                        {mutationSMSVerify.isLoading
                          ? 'Updating MFA...'
                          : 'Verify Code and Set as MFA'}
                      </Button>

                      {mutationSMSVerify.isSuccess ? (
                        <Alert severity="success">
                          Preference has been set!
                        </Alert>
                      ) : null}
                    </Stack>
                  </Box>
                </Paper>
              </Stack>
            ) : null}

            {selected === 'SOFTWARE_TOKEN_MFA' ? (
              <Stack spacing={1}>
                <Paper
                  elevation={0}
                  sx={{ p: 1, border: '1px solid #00000022' }}
                >
                  <Stack spacing={1} direction="row">
                    <HelpOutlineIcon fontSize="small" color="info" />

                    <Box sx={{ fontSize: 14, color: '#212121' }}>
                      Authenticator apps (e.g., Google Authenticator, Microsoft
                      Authenticator) generate time-based, one-time passcodes
                      (TOTP) on a user's smartphone. Users must input the
                      current code displayed in the app to complete the
                      authentication process.
                    </Box>
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={{ border: '1px solid #00000022' }}>
                  <Box sx={{ p: 1 }}>
                    <Stack spacing={1}>
                      <Stack spacing={1} direction="row">
                        <Box>
                          <Box
                            sx={{
                              fontWeight: 'bold',
                              fontFamily: 'Roboto Mono',
                              border: '1px solid #000',
                              borderRadius: 50,
                              height: 22,
                              width: 22,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            1
                          </Box>
                        </Box>

                        <Box>
                          Scan the QR Code or copy and paste the code into your
                          Authenticator App. Once you have added this code to
                          your Authenticator App your App will generate a TOTP
                          code that will need to be verified in the next step.
                        </Box>
                      </Stack>

                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {queryTOTPCode.isFetching ? (
                          <Box
                            sx={{
                              height: 240,
                              width: 240,
                              backgroundColor: '#000',
                            }}
                          />
                        ) : queryTOTPCode.data ? (
                          <QrCode
                            value={createOtpAuthUrl({
                              issuer: 'TheAlliance',
                              account: user.getUsername(),
                              secret: queryTOTPCode.data,
                            })}
                            size={240}
                          />
                        ) : null}
                      </Box>

                      <Stack spacing={1} direction="row" alignItems="center">
                        <Tooltip
                          title={
                            copied
                              ? 'Copied to clipboard!'
                              : 'Copy code to clipboard'
                          }
                          placement="right"
                          arrow
                        >
                          <Button
                            variant="outlined"
                            color="info"
                            startIcon={<ContentCopyOutlinedIcon />}
                            onClick={() => {
                              copy(queryTOTPCode.data || '');
                              setCopied(true);
                            }}
                            onMouseLeave={() => setCopied(false)}
                          >
                            Copy
                          </Button>
                        </Tooltip>

                        <Box
                          sx={{
                            flex: 1,
                            fontFamily: 'Roboto Mono',
                            fontSize: 12,
                            wordBreak: 'break-word',
                          }}
                        >
                          {queryTOTPCode.isFetching
                            ? '000000000000000000000'
                            : queryTOTPCode.data}
                        </Box>
                      </Stack>

                      {queryTOTPCode.isError ? (
                        <Alert
                          severity="error"
                          onClose={() => queryTOTPCode.refetch()}
                        >
                          {getErrorMessage({ error: queryTOTPCode.error })}
                        </Alert>
                      ) : null}
                    </Stack>
                  </Box>

                  <Divider />

                  <Box sx={{ p: 1 }}>
                    <Stack spacing={1}>
                      <Stack spacing={1} direction="row">
                        <Box>
                          <Box
                            sx={{
                              fontWeight: 'bold',
                              fontFamily: 'Roboto Mono',
                              border: '1px solid #000',
                              borderRadius: 50,
                              height: 22,
                              width: 22,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            2
                          </Box>
                        </Box>

                        <Box sx={{ pb: 1 }}>
                          Add the TOTP code generated from your Authenticator
                          App to verify that it is setup correctly.
                        </Box>
                      </Stack>

                      <TextField
                        id="multi-factor-info-totp-verify"
                        fullWidth
                        placeholder="000000"
                        autoComplete="one-time-code"
                        label="Code from Authenticator App"
                        value={verifyTOTPCode}
                        onChange={(event) => {
                          setVerifyTOTPCode(
                            event.target.value.replace(/\D/g, ''),
                          );
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { fontFamily: 'Roboto Mono' } }}
                      />

                      {mutationTOTPVerify.isError ? (
                        <Alert
                          severity="error"
                          onClose={() => mutationTOTPVerify.reset()}
                        >
                          {getErrorMessage({
                            error: mutationTOTPVerify.error,
                          })}
                        </Alert>
                      ) : null}

                      <Button
                        disabled={
                          mutationTOTPVerify.isLoading ||
                          mutationTOTPVerify.isError ||
                          !verifyTOTPCode.trim()
                        }
                        fullWidth
                        variant="contained"
                        disableElevation
                        size="large"
                        color="info"
                        startIcon={<PinIcon />}
                        onClick={() => mutationTOTPVerify.mutate()}
                      >
                        {mutationTOTPVerify.isLoading
                          ? 'Updating MFA...'
                          : 'Verify Code and Set as MFA'}
                      </Button>

                      {mutationTOTPVerify.isSuccess ? (
                        <Alert severity="success">
                          Preference has been set!
                        </Alert>
                      ) : null}
                    </Stack>
                  </Box>
                </Paper>
              </Stack>
            ) : null}
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}
